import ByStatusChart from "@/features/control-results/components/charts/by-status-chart";
import PopulationChart from "@/features/control-results/components/charts/population-chart";
import ControlDescription from "@/features/control-results/components/control-description";
import { ControlResultConclusionFilters } from "@/features/control-results/components/control-result-conclusion-filters";
import { ControlResultOwnerFilter } from "@/features/control-results/components/control-result-owner-filter";
import { ControlResultPriorityFilter } from "@/features/control-results/components/control-result-priority-filter";
import { ControlResultStatusFilter } from "@/features/control-results/components/control-result-status-filter";
import { OwnershipSelector } from "@/features/control-results/components/ownership-selector";
import { PrioritySelector } from "@/features/control-results/components/priority-selector";
import { selectControlResultsViewModel } from "@/features/control-results/view-models/select-control-results.view-model";
import { WorkflowFormProvider } from "@/features/workflows/components/workflow-form-context";
import { WorkflowTransition } from "@/features/workflows/components/workflow-transition";
import { Breadcrumb } from "@spz/ui/components/breadcrumb";
import { Button } from "@spz/ui/components/button";
import { Hint } from "@spz/ui/components/hint";
import {
  PageSelectionBanner,
  PageSelectionMode,
} from "@spz/ui/components/page-selection-banner";
import SelectionCounter from "@spz/ui/components/selection-counter";
import { Table } from "@spz/ui/components/table";
import { DataTableBody } from "@spz/ui/components/table/data-table-body";
import { DataTableHeader } from "@spz/ui/components/table/data-table-header";
import { DataTablePagination } from "@spz/ui/components/table/data-table-pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@spz/ui/components/tooltip";
import { Typography } from "@spz/ui/components/typography";
import React from "react";
import { useTranslation } from "react-i18next";

function ControlResults() {
  const { t } = useTranslation();

  const {
    analysisId,
    analysisName,
    controlId,
    controlName,
    table,
    periodName,
    controlDescription,
    charts,
    company,
    isLoading,
    handleRowClick,
    dynamicFilters,
    controlResultSteps,
    conclusionSections,
    pagination,
    bulkSelection: {
      showBanner,
      showAllSelectedBanner,
      currentPageCount,
      selectAllAcrossFiltered,
      clearSelection,
      selectedCount,
      handleOwnershipChange,
      canManageOwnership,
      isBulkActionInProgress,
      selectionAnalysis,
      filteredTransitions,
      handleTransitionSubmit,
      canManagePriority,
      handlePriorityChange,
    },
  } = selectControlResultsViewModel(t);

  const breadcrumbItems = [
    {
      label: `${company.name} / ${periodName} / ${analysisName}`,
      to: `/controls?analysis=${analysisId}`,
    },
    {
      label: controlId,
      isCurrent: true,
      "data-testid": "control-results-id",
    },
  ];
  return (
    <div className="grid grid-rows-15">
      <Breadcrumb
        className="mb-2"
        items={breadcrumbItems}
        data-testid="control-results-back-button"
      />

      {/* Header */}
      <Typography
        variant="h5"
        trans="control_results_list_title"
        transParams={{ controlName: t(controlName) }}
        className="mb-4"
        data-testid="control-results-name"
      />
      {/* Main content */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left Area */}
        <div data-testid="control-section-info">
          <ControlDescription sections={controlDescription.sections} />
        </div>

        {/* Right Area grid-rows-8 with gap-4 */}
        <div className="flex gap-4 h-[245px]">
          <ByStatusChart
            workflowStats={charts.workflowStats}
            controlStatuses={controlResultSteps}
            totalCount={pagination.totalCount}
            loading={isLoading}
          />

          <PopulationChart loading={charts.loading} data={charts?.population} />
        </div>
      </div>
      <div className="flex items-center gap-2 content-center mt-6 mb-4">
        <Typography variant="h5" trans="results" />
        {selectedCount !== 0 && (
          <SelectionCounter
            selected={selectedCount}
            total={pagination.totalCount}
          />
        )}
      </div>
      {selectedCount === 0 && (
        <div className="mb-4 flex flex-wrap gap-3 z-10">
          <ControlResultStatusFilter table={table} steps={controlResultSteps} />
          <ControlResultConclusionFilters
            table={table}
            conclusionSections={conclusionSections}
          />
          <ControlResultPriorityFilter table={table} />
          <ControlResultOwnerFilter table={table} companyId={company.id} />
          {dynamicFilters}
          <Button
            variant="ghost"
            leftIcon="funnel"
            onClick={() => {
              table.resetColumnFilters();
            }}
          >
            {t("clear_filters")}
          </Button>
        </div>
      )}
      {selectedCount > 0 && (
        <div className="mb-5 space-x-2 flex items-center">
          {selectionAnalysis.isHomogeneous &&
            filteredTransitions &&
            filteredTransitions.length > 0 && (
              <div className="flex gap-2">
                {filteredTransitions.map((transition) => (
                  <WorkflowFormProvider
                    key={transition.id}
                    companyId={company.id}
                    schema={transition}
                    selectedCount={selectedCount}
                  >
                    <Tooltip open={isBulkActionInProgress ? undefined : false}>
                      <TooltipTrigger asChild>
                        <span className="inline-block">
                          <WorkflowTransition
                            onSubmit={async (formData) =>
                              await handleTransitionSubmit(
                                transition.id,
                                formData,
                              )
                            }
                            disabled={isBulkActionInProgress}
                            closeImmediately={true}
                          />
                        </span>
                      </TooltipTrigger>
                      {isBulkActionInProgress && (
                        <TooltipContent>
                          {t("bulk_action_in_progress")}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </WorkflowFormProvider>
                ))}
              </div>
            )}
          {canManageOwnership && (
            <Tooltip open={isBulkActionInProgress ? undefined : false}>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <OwnershipSelector
                    companyId={company.id}
                    value={null}
                    onChange={handleOwnershipChange}
                    readonly={isBulkActionInProgress}
                    isBulkSelection={true}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{t("bulk_action_in_progress")}</TooltipContent>
            </Tooltip>
          )}
          {canManagePriority && (
            <Tooltip open={isBulkActionInProgress ? undefined : false}>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <PrioritySelector
                    value={null}
                    onChange={handlePriorityChange}
                    readonly={isBulkActionInProgress}
                    isBulkSelection={true}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{t("bulk_action_in_progress")}</TooltipContent>
            </Tooltip>
          )}

          {!selectionAnalysis.isHomogeneous && (
            <Hint text={t("mixed_selection_hint")} />
          )}
        </div>
      )}
      {(showBanner || showAllSelectedBanner) && (
        <PageSelectionBanner
          mode={
            showBanner
              ? PageSelectionMode.pageAllSelected
              : PageSelectionMode.allAcrossSelected
          }
          x={currentPageCount}
          y={pagination.totalCount}
          onSelectAllAcross={selectAllAcrossFiltered}
          onClear={clearSelection}
          className="mb-2"
        />
      )}

      <Table className="table-fixed" data-testid="control-section-results">
        <DataTableHeader table={table} />
        <DataTableBody
          table={table}
          onRowClick={handleRowClick}
          isLoading={isLoading}
        />
      </Table>

      {/* Result list table */}

      {/* FIXME : display of footer will become optional depending on the totalCount */}
      {/* Footer */}
      <DataTablePagination
        table={table}
        entityLabel="pagination_entity_control_results"
        isLoading={isLoading}
      />
    </div>
  );
}

export default React.memo(ControlResults);
